/**
 * useHandshakes.ts — React Query hooks for Handshake state machine.
 *
 * The list/detail/transition pattern. Every state-changing mutation
 * invalidates the list query and updates the detail cache with the
 * returned doc (so the drawer reflects the new state immediately).
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import {
  handshakeService,
  type Handshake,
  type HandshakeListFilters,
  type HandshakeListResponse,
  type CreateHandshakeInput,
  type AcknowledgeInput,
  type CustomerReceiptInput,
} from "../services/handshakeService";

export const handshakeKeys = {
  all: ["handshakes"] as const,
  lists: () => [...handshakeKeys.all, "list"] as const,
  list: (filters: HandshakeListFilters) =>
    [...handshakeKeys.lists(), filters] as const,
  details: () => [...handshakeKeys.all, "detail"] as const,
  detail: (id: string) => [...handshakeKeys.details(), id] as const,
};

// ─── Queries ───────────────────────────────────────────────────────

export function useHandshakes(
  filters: HandshakeListFilters = {},
  options?: Omit<
    UseQueryOptions<HandshakeListResponse>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery({
    queryKey: handshakeKeys.list(filters),
    queryFn: () => handshakeService.list(filters),
    staleTime: 15_000,
    ...options,
  });
}

export function useHandshake(
  id: string | null,
  options?: Omit<UseQueryOptions<Handshake>, "queryKey" | "queryFn">,
) {
  return useQuery({
    queryKey: handshakeKeys.detail(id || ""),
    queryFn: () => handshakeService.get(id!),
    enabled: !!id,
    staleTime: 10_000,
    ...options,
  });
}

// ─── Mutations ─────────────────────────────────────────────────────

// Internal helper — every state transition handler does the same
// invalidation + cache write.
function invalidateAndCache(qc: ReturnType<typeof useQueryClient>) {
  return (data: Handshake) => {
    qc.invalidateQueries({ queryKey: handshakeKeys.lists() });
    qc.setQueryData(handshakeKeys.detail(data._id), data);
  };
}

export function useCreateHandshake() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateHandshakeInput) => handshakeService.create(input),
    onSuccess: invalidateAndCache(qc),
  });
}

export function useAcknowledgeHandshake() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input?: AcknowledgeInput }) =>
      handshakeService.acknowledge(id, input || {}),
    onSuccess: invalidateAndCache(qc),
  });
}

export function useConfirmHandshake() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      handshakeService.confirm(id, notes),
    onSuccess: invalidateAndCache(qc),
  });
}

export function useCloseHandshake() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      handshakeService.close(id, notes),
    onSuccess: invalidateAndCache(qc),
  });
}

export function useRejectHandshake() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      handshakeService.reject(id, reason),
    onSuccess: invalidateAndCache(qc),
  });
}

export function useCancelHandshake() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      handshakeService.cancel(id, reason),
    onSuccess: invalidateAndCache(qc),
  });
}

export function useDelegateHandshake() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      cashierUserId,
    }: {
      id: string;
      cashierUserId: string;
    }) => handshakeService.delegate(id, cashierUserId),
    onSuccess: invalidateAndCache(qc),
  });
}

export function usePostMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) =>
      handshakeService.postMessage(id, body),
    onSuccess: invalidateAndCache(qc),
  });
}

export function useRecordCustomerReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: CustomerReceiptInput }) =>
      handshakeService.recordCustomerReceipt(id, input),
    onSuccess: invalidateAndCache(qc),
  });
}
